/**
 * EncryptionCodec — decrypts inbound WebPubSub frames.
 *
 * ## Why decoding may try a second key
 * The session encryption key is regenerated on every token refresh: `KDF`
 * (utils/encryption.ts) mixes in `v4` and `Date.now`, so each `/session/update` writes
 * a brand-new random `k` over the previous one.
 *
 * ## Why a `json` frame skips decryption
 * A frame the backend publishes as `application/json` (the `chunked_message` slices of
 * an oversized frame, today) is not encrypted. Web PubSub marks it `dataType: 'json'`
 * but may deliver the body as a raw string rather than a parsed object, and a string
 * handed to `decryptResponse` fails as ciphertext — so it is parsed, never decrypted.
 */

import { IS_ENCRYPTION_ENABLED } from 'src/config';
import { getEncryptParams } from 'src/keycloak';
import { decryptResponse } from 'src/services/utils';

export interface EncryptionCodec {
  decode(raw: unknown): unknown;
  noteCurrentKey?(): void;
  readonly isEncryptionEnabled: boolean;
}

const KEY_HISTORY = 2;

interface KeyEntry {
  id: string;
  key: unknown;
}

export function createEncryptionCodec(): EncryptionCodec {
  let current: KeyEntry | undefined;
  const superseded: KeyEntry[] = [];

  const identify = (key: unknown): string => {
    try {
      return JSON.stringify(key) ?? 'undefined';
    } catch {
      return String(key);
    }
  };

  const noteCurrentKey = (): void => {
    const key = getEncryptParams()?.k;
    if (key === undefined || key === null) {
      return;
    }
    const id = identify(key);
    if (current?.id === id) {
      return;
    }
    if (current) {
      superseded.unshift(current);
      if (superseded.length > KEY_HISTORY) {
        superseded.length = KEY_HISTORY;
      }
    }
    current = { id, key };
  };

  const attempt = (raw: unknown, key: unknown): unknown => {
    const result = decryptResponse(raw as never, key as never);
    if (!IS_ENCRYPTION_ENABLED && typeof result === 'string') {
      try {
        return JSON.parse(result);
      } catch {
        return undefined;
      }
    }
    return result;
  };

  const isJsonFrame = (raw: unknown): raw is { data?: unknown } =>
    typeof raw === 'object' &&
    raw !== null &&
    (raw as { dataType?: unknown }).dataType === 'json';

  const readJsonFrame = ({ data }: { data?: unknown }): unknown => {
    if (typeof data !== 'string') {
      return data ?? undefined;
    }
    try {
      return JSON.parse(data) as unknown;
    } catch {
      return undefined;
    }
  };

  return {
    isEncryptionEnabled: IS_ENCRYPTION_ENABLED,
    noteCurrentKey,

    decode(raw: unknown): unknown {
      noteCurrentKey();

      if (isJsonFrame(raw)) {
        return readJsonFrame(raw);
      }

      const result = current ? attempt(raw, current.key) : undefined;
      if (result !== undefined || !IS_ENCRYPTION_ENABLED) {
        return result;
      }

      for (const entry of superseded) {
        const older = attempt(raw, entry.key);
        if (older !== undefined) {
          return older;
        }
      }
      return undefined;
    }
  };
}
